import { writable, type Writable, type Readable } from 'svelte/store';
import { isTabDisabled, type SidepanelTabsPropsTypesExtended } from './sidepanel-tabs-helpers';
import type { Device } from 'src/types/Device';
import { TabId, type BaseTab, type TabsState, type TabClickEvent } from './sidepanel-types';
import Addons from 'src/components/Sidepanel/SidePanelLayoutProvider/SidePanelDeviceLayout/CommonDeviceTabs/Addons.svelte';
import { hasAddonPeripherals } from 'src/helpers/addons/addon_helpers';

/**
 * TabsManager manages tab state and navigation using stable tab IDs.
 * Uses a private Svelte store for reactive state management.
 */
export class TabsManager {
	private tabsState: Writable<TabsState>;
	private baseTabs: BaseTab[];
	private activeTabs: BaseTab[];
	private tabIndexById: Map<TabId, number>;

	private isValidIndex(index: number): boolean {
		return index >= 0 && index < this.activeTabs.length;
	}

	private getTabsForDevice(device: Device | undefined): BaseTab[] {
		const tabs = [...this.baseTabs];
		if (!device || !hasAddonPeripherals(device)) {
			return tabs;
		}

		if (tabs.some((tab) => tab.id === TabId.ADDONS)) {
			return tabs;
		}

		const addonsTab: BaseTab = {
			id: TabId.ADDONS,
			icon: 'fak fa-thin-sensor-on',
			component: Addons,
		};
		const notificationsIndex = tabs.findIndex((tab) => tab.id === TabId.NOTIFICATIONS);
		if (notificationsIndex >= 0) {
			tabs.splice(notificationsIndex, 0, addonsTab);
		} else {
			tabs.push(addonsTab);
		}

		return tabs;
	}

	constructor(baseTabs: BaseTab[], initial?: Partial<TabsState>) {
		this.baseTabs = baseTabs;
		this.activeTabs = [...baseTabs];

		this.tabIndexById = new Map(baseTabs.map((tab, index) => [tab.id, index]));

		this.tabsState = writable<TabsState>({
			tabIndex: initial?.tabIndex ?? 0,
			openedEventTimestamp: initial?.openedEventTimestamp ?? null,
			activeHistoryButton: initial?.activeHistoryButton ?? null,
		});
	}

	/**
	 * Returns a read-only store for subscribing to tab state changes.
	 * Use $tabsState to access reactive state in Svelte components.
	 * @returns Read-only store for tab state
	 */
	public getTabsState = (): Readable<TabsState> => {
		return { subscribe: this.tabsState.subscribe };
	};

	/**
	 * Gets the tab index for a given tab ID.
	 * @param tabId - The stable tab ID
	 * @returns The tab index, or -1 if not found
	 */
	public getTabIndexById = (tabId: TabId): number => {
		return this.tabIndexById.get(tabId) ?? -1;
	};

	/**
	 * Navigates to a tab by its ID with optional state updates.
	 * - openedEventTimestamp: pass when navigating to Activity Log tab to open a specific event.
	 * - activeHistoryButton: used only when layout still uses the legacy combined History tab with sub-buttons (1=Statistics, 2=Activity Log).
	 */
	public navigateToTabById = (
		tabId: TabId,
		options?: { openedEventTimestamp?: number; activeHistoryButton?: number },
	): void => {
		const index = this.getTabIndexById(tabId);
		if (index >= 0) {
			this.navigateToTabIndex(index, options);
		}
	};

	/**
	 * Navigates to a tab by its index with optional state updates.
	 * Use openedEventTimestamp when navigating to Activity Log; activeHistoryButton for legacy combined History tab.
	 */
	public navigateToTabIndex = (
		index: number,
		options?: { openedEventTimestamp?: number; activeHistoryButton?: number },
	): void => {
		if (this.isValidIndex(index)) {
			this.tabsState.update((state) => ({
				...state,
				tabIndex: index,
				openedEventTimestamp:
					options?.openedEventTimestamp !== undefined
						? options.openedEventTimestamp
						: state.openedEventTimestamp,
				activeHistoryButton:
					options?.activeHistoryButton !== undefined
						? options.activeHistoryButton
						: state.activeHistoryButton,
			}));
		}
	};

	public onTabClick = (e: TabClickEvent): void => {
		const { index } = e.detail;
		if (this.isValidIndex(index)) {
			this.tabsState.update((state) => ({
				...state,
				tabIndex: index,
				openedEventTimestamp: null,
				activeHistoryButton: null,
			}));
		}
	};

	/**
	 * Resets the active tab to index 0 and clears optional state.
	 */
	public resetActiveTab = (): void => {
		this.tabsState.update((state) => ({
			...state,
			tabIndex: 0,
			openedEventTimestamp: null,
			activeHistoryButton: null,
		}));
	};

	/**
	 * Generates tabs configuration with isActive and disabled states.
	 * Uses current internal tabIndex for isActive calculation.
	 * @param device - Device object (can be undefined)
	 * @param deviceState - Device state from getDeviceState
	 * @returns Tabs array with isActive and disabled properties
	 */
	public getTabsConfig = (
		tabIndex: number,
		device: Device | undefined,
		deviceState: any,
	): SidepanelTabsPropsTypesExtended['tabs'] => {
		this.activeTabs = this.getTabsForDevice(device);
		this.tabIndexById = new Map(this.activeTabs.map((tab, index) => [tab.id, index]));

		return this.activeTabs.map((tab, index) => {
			const isActive = tabIndex === index;
			const icon =
				tab.id === TabId.ADDONS
					? isActive
						? 'fak fa-solid-sensor-on'
						: 'fak fa-thin-sensor-on'
					: tab.icon;

			return {
				...tab,
				icon,
				isActive,
				disabled: tab.id ? isTabDisabled(device, deviceState, tab.id) : false,
			};
		});
	};
}
